import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useGoogleAds } from '../../contexts/providers/GoogleAdsProvider/GoogleAdsContext'
import { AdComponentProps } from '../types'

const GoogleAdContainer = styled.div`
  > div {
    position: relative;
    z-index: 2;
  }
`
const GoogleAd: React.FC<AdComponentProps> = ({
  slot,
  className = '',
  responsive = true,
  onAdLoad,
  onAdError,
  onSlotRenderEnded,
}) => {
  const { isInitialized, registerGPTSlot, destroyAd } = useGoogleAds()
  const adRef = useRef<HTMLDivElement | null>(null)
  const [adSlot, setAdSlot] = useState<googletag.Slot | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (adSlot) {
      registerGPTSlot(slot.id, adSlot)
    }

    return () => {
      destroyAd(slot.id)
    }
  }, [slot.id, registerGPTSlot, destroyAd, adSlot])

  useEffect(() => {
    if (!isInitialized || !adRef.current || isLoaded) {
      return
    }

    const defineSlot = () => {
      window.googletag.cmd.push(() => {
        try {
          destroyAd(slot.id)
          // Define the ad slot
          const newSlot = window.googletag.defineSlot(
            slot.path,
            slot.size as googletag.GeneralSize,
            slot.id
          )

          if (!newSlot) {
            throw new Error(`Failed to define slot ${slot.id}`)
          }

          newSlot.addService(window.googletag.pubads())

          // Add targeting if provided
          if (slot.targeting) {
            Object.entries(slot.targeting).forEach(([key, value]) => {
              newSlot.setTargeting(key, value)
            })
          }

          // Display the ad
          window.googletag.display(slot.id)

          setAdSlot(newSlot)
          setIsLoaded(true)

          if (onAdLoad) {
            onAdLoad()
          }
        } catch (error) {
          console.error('Error defining ad slot:', error)
          if (onAdError) {
            onAdError(
              error instanceof Error ? error : new Error('Unknown error')
            )
          }
        }
      })
    }

    defineSlot()
  }, [isInitialized, slot, isLoaded, onAdLoad, onAdError, adSlot, destroyAd])

  // Set up slot render ended event listener
  useEffect(() => {
    if (!isInitialized || !onSlotRenderEnded) {
      return
    }

    const handleSlotRenderEnded = (
      event: googletag.events.SlotRenderEndedEvent
    ) => {
      // Only call the callback if this event is for our slot
      if (event.slot.getSlotElementId() === slot.id) {
        onSlotRenderEnded(event)
      }
    }

    window.googletag.cmd.push(() => {
      const pubads = window.googletag.pubads()
      pubads.addEventListener('slotRenderEnded', handleSlotRenderEnded)
    })

    return () => {
      if (window.googletag && window.googletag.pubads) {
        window.googletag.cmd.push(() => {
          const pubads = window.googletag.pubads()
          pubads.removeEventListener('slotRenderEnded', handleSlotRenderEnded)
        })
      }
    }
  }, [isInitialized, slot.id, onSlotRenderEnded])

  return (
    <GoogleAdContainer
      ref={adRef}
      id={slot.id}
      className={`google-ad-container ${className}`}
      style={{
        minHeight: responsive ? 'auto' : '250px',
        display: 'block',
        textAlign: 'center',
      }}
    />
  )
}
export default GoogleAd
